import React, { useState } from 'react';
import {
  History,
  Sparkles,
  RefreshCw,
  Wrench,
  Shield,
  ChevronDown,
  ChevronUp,
  Star,
} from 'lucide-react';
import {
  CHANGELOG_CATEGORY_LABELS,
  CHANGELOG_VERSIONS,
  ChangelogCategory,
  ChangelogItem,
  ChangelogVersion,
} from '../data/changelogData';
import { APP_VERSION_BADGE } from '../data/appVersion';

const CATEGORY_ICONS: Record<ChangelogCategory, React.ComponentType<{ className?: string }>> = {
  added: Sparkles,
  changed: RefreshCw,
  fixed: Wrench,
  security: Shield,
};

const CATEGORY_ORDER: ChangelogCategory[] = ['added', 'changed', 'fixed', 'security'];

function HighlightBadges({ labels }: { labels: string[] }) {
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {labels.map((label) => (
        <span
          key={label}
          className="inline-flex items-center rounded-full border border-accent-cyan/25 bg-accent-cyan/10 px-2.5 py-1 text-xs font-medium text-accent-cyan"
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function ChangelogEntry({ item }: { item: ChangelogItem }) {
  if (item.important || item.label) {
    return (
      <li className="flex flex-wrap items-start gap-2 text-sm leading-relaxed">
        {item.label && (
          <span className="inline-flex shrink-0 items-center rounded-full border border-accent-cyan/30 bg-accent-cyan/15 px-2.5 py-0.5 text-xs font-semibold text-accent-cyan">
            {item.label}
          </span>
        )}
        <span className={item.important ? 'text-text-primary font-medium' : 'text-text-secondary'}>
          {item.text}
        </span>
        {item.important && !item.label && (
          <Star size={12} className="shrink-0 text-accent-cyan mt-1" aria-hidden="true" />
        )}
      </li>
    );
  }

  return (
    <li className="flex items-start gap-2.5 text-sm text-text-secondary leading-relaxed">
      <span
        className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-cyan/70"
        aria-hidden="true"
      />
      <span>{item.text}</span>
    </li>
  );
}

function VersionCard({
  entry,
  defaultExpanded,
}: {
  entry: ChangelogVersion;
  defaultExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const sectionKeys = CATEGORY_ORDER.filter((key) => (entry.sections[key]?.length ?? 0) > 0);

  const importantItems = sectionKeys.flatMap((category) =>
    (entry.sections[category] ?? []).filter((item) => item.important),
  );

  return (
    <article className="rounded-2xl border border-border-primary bg-bg-card shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="w-full flex items-start justify-between gap-4 p-5 md:p-6 text-right hover:bg-bg-secondary/30 transition-colors"
        aria-expanded={expanded}
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="inline-flex items-center rounded-full bg-accent-cyan/15 px-3 py-1 text-sm font-semibold text-accent-cyan">
              {entry.versionLabel}
            </span>
            <span className="text-sm text-text-tertiary">{entry.releaseDate}</span>
          </div>
          <h2 className="text-lg md:text-xl font-bold text-text-primary">{entry.title}</h2>
          {entry.summary && (
            <p className="mt-2 text-sm text-text-secondary leading-relaxed">{entry.summary}</p>
          )}
          {entry.highlights && entry.highlights.length > 0 && (
            <HighlightBadges labels={entry.highlights} />
          )}
        </div>
        <span className="shrink-0 text-text-tertiary mt-1" aria-hidden="true">
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-border-primary px-5 md:px-6 pb-5 md:pb-6 pt-4 space-y-5">
          {importantItems.length > 0 && (
            <section aria-labelledby={`${entry.version}-important`}>
              <h3
                id={`${entry.version}-important`}
                className="flex items-center gap-2 text-base font-semibold text-text-primary mb-3"
              >
                <span className="inline-flex rounded-lg bg-accent-cyan/10 p-1.5 text-accent-cyan">
                  <Star className="w-4 h-4" aria-hidden="true" />
                </span>
                مهم‌ترین تغییرات
              </h3>
              <ul className="space-y-2.5">
                {importantItems.map((item) => (
                  <ChangelogEntry key={`${item.label}-${item.text}`} item={item} />
                ))}
              </ul>
            </section>
          )}

          {sectionKeys.map((category) => {
            const Icon = CATEGORY_ICONS[category];
            const items = (entry.sections[category] ?? []).filter((item) => !item.important);
            if (items.length === 0) return null;
            return (
              <section key={category} aria-labelledby={`${entry.version}-${category}`}>
                <h3
                  id={`${entry.version}-${category}`}
                  className="flex items-center gap-2 text-base font-semibold text-text-primary mb-3"
                >
                  <span className="inline-flex rounded-lg bg-accent-cyan/10 p-1.5 text-accent-cyan">
                    <Icon className="w-4 h-4" aria-hidden="true" />
                  </span>
                  {CHANGELOG_CATEGORY_LABELS[category]}
                </h3>
                <ul className="space-y-2 mr-1">
                  {items.map((item) => (
                    <ChangelogEntry key={`${category}-${item.label}-${item.text}`} item={item} />
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </article>
  );
}

const ChangelogDashboard: React.FC = () => {
  const latestHighlights = CHANGELOG_VERSIONS[0]?.highlights ?? [];

  return (
    <div dir="rtl" className="container-dashboard pb-12">
      <div className="space-y-8 md:space-y-10 animate-enter-up max-w-4xl mx-auto">
        <header className="relative overflow-hidden rounded-2xl border border-border-primary bg-gradient-to-br from-bg-card via-bg-card to-accent-cyan/5 p-6 md:p-10 shadow-sm">
          <div
            className="absolute top-0 left-0 h-32 w-32 rounded-full bg-accent-cyan/10 blur-3xl"
            aria-hidden="true"
          />
          <div className="relative">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-cyan/15 px-3 py-1 text-sm font-semibold text-accent-cyan">
                <History size={14} aria-hidden="true" />
                {APP_VERSION_BADGE}
              </span>
            </div>
            <h1 className="title-dashboard mb-3">تاریخچه تغییرات</h1>
            <p className="text-lg text-text-secondary max-w-3xl leading-relaxed">
              فهرست تغییرات هر نسخه از Smart Insight — از قابلیت‌های جدید تا بهبودها و رفع مشکلات.
            </p>
            {latestHighlights.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-text-tertiary mb-2">موضوعات کلیدی نسخه جاری</p>
                <HighlightBadges labels={latestHighlights} />
              </div>
            )}
          </div>
        </header>

        <div className="space-y-4">
          {CHANGELOG_VERSIONS.map((entry, index) => (
            <VersionCard key={entry.version} entry={entry} defaultExpanded={index === 0} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChangelogDashboard;
